<template>
  <div class="page-container brand-editor">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar marca" : "Crear marca" }}
      </h1>
    </div>
    <div class="container px-0">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8">
          <b-card tag="article" no-body style="max-width: 640px" class="custom-card">
            <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
              <h3>
                {{
                  editing
                    ? "Datos de la marca"
                    : "Datos de la nueva marca"
                }}
              </h3>
            </b-card-header>
            <b-card-body class="custom-card-body p-0">

              <form class="custom-form" @submit.prevent="save" novalidate>
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Información general</h2>
                  <p class="custom-card-description">
                    Ingresa los datos
                    {{ editing ? "de la marca" : "de la nueva marca" }}
                  </p>

                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="name">Nombre de marca *</label>

                        <FormInput id="name" type="text" placeholder="Ingresar nombre" :show-error="$v.brand.name.$error &&
                          !$v.brand.name.required
                          " v-model="brand.name" />

                        <form-error message="Nombre es requerido" v-if="
                          $v.brand.name.$error &&
                          !$v.brand.name.required
                        "></form-error>
                      </div>

                      <div class="form-group">
                        <label for="slug">Slug *</label>

                        <FormInput id="slug" type="text" placeholder="Ingresar slug" :show-error="$v.brand.slug.$error &&
                          !$v.brand.slug.required
                          " v-model="brand.slug" />

                        <form-error message="Slug es requerido" v-if="
                          $v.brand.slug.$error &&
                          !$v.brand.slug.required
                        "></form-error>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="shortDescription">Descripción corta</label>

                        <textarea id="shortDescription" placeholder="Escriba aquí" class="custom-form-control"
                          v-model="brand.shortDescription"></textarea>
                      </div>

                      <div class="form-group">
                        <label for="description">Descripción</label>
                        <global-trumbowyg v-model="brand.description" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-section">
                  <h2 class="custom-card-subtitle">Foto de la marca</h2>

                  <p class="custom-card-description">
                    Agregue una foto de la marca en el siguiente tamaño:
                  </p>

                  <UploadImages :maxFiles="1" :files="setOldFile" @onChange="onChange" @remove="removeImage" />
                </div>

                <div class="form-section">
                  <h2 class="custom-card-subtitle">Datos SEO</h2>

                  <p class="custom-card-description">
                    Ingresa los siguientes datos SEO
                  </p>

                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="seoTitle">Título SEO ({{ brand.seoTitle ? brand.seoTitle.length : 0 }})</label>
                        <FormInput id="seoTitle" type="text" placeholder="Ingresar título SEO"
                          v-model="brand.seoTitle" />
                        <form-error
                        message="El título SEO puede tener hasta 70 caracteres"
                          v-if="$v.brand.seoTitle.$error && !$v.brand.seoTitle.maxLength"
                        ></form-error>
                      </div>

                    </div>

                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="seoKeywords">Palabras Clave</label>
                        <FormInput id="seoKeywords" type="text" placeholder="Ingresar palabras clave"
                          v-model="brand.seoKeywords" />
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group mb-0">
                        <label for="seoMeta">Meta description ({{ brand.seoMeta ? brand.seoMeta.length : 0 }})</label>
                        <textarea id="seoMeta" placeholder="Escriba aquí" class="custom-form-control"
                          v-model="brand.seoMeta"></textarea>
                        <form-error
                          message="El meta description puede tener hasta 320 caracteres"
                          v-if="$v.brand.seoMeta.$error && !$v.brand.seoMeta.maxLength"
                        ></form-error>
                      </div>
                    </div>

                    <div class="form-group-buttons text-right">
                      <router-link :to="{ name: 'config-brands-list' }" class="button button-light">
                        {{ editing ? "Volver" : "Cancelar" }}
                      </router-link>
                      <button class="button button-primary" type="submit">
                        Guardar
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Alert,
  Constants,
  FormError,
  FormInput,
  StringUtil,
  UploadImages
} from "@wizepe/wize-admin";

import GlobalTrumbowyg from "@/core/components/common/GlobalTrumbowyg";
import UploadImagesMixin from '@/core/mixins/UploadImagesMixin';
import { BrandService } from "@/core/services";
import { required, maxLength } from "vuelidate/lib/validators";

export default {
  mixins: [UploadImagesMixin],
  data() {
    return {
      brand: {
        name: null,
        slug: null,
        assetId: null,
        preview: null,
        shortDescription: null,
        description: null,
        seoTitle: null,
        seoKeywords: null,
        seoMeta: null,
      },
      editing: false,
    };
  },
  components: {
    FormError,
    FormInput,
    UploadImages,
    GlobalTrumbowyg
  },
  validations: {
    brand: {
      name: { required },
      slug: { required },
      seoTitle: {maxLength: maxLength(70)},
      seoMeta: {maxLength: maxLength(320)}
    },
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "brand.name": function (val) {
      if (val && !this.editing) {
        this.brand.slug = StringUtil.toSlug(val);
      }
    },
  },
  methods: {
    async onChange(files) {
      try {
        let uploadedfiles = await this.uploadFiles(files)

        this.brand.assetId = uploadedfiles[0].id;
      } catch (error) {
        alert('Error')
        console.error(error);
      }
    },
    removeImage() {
      this.brand.image = null
      this.brand.assetId = null
    },
    async save() {
      this.$v.$touch();

      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      this.$store.dispatch("app/loading", true)

      let brand = { ...this.brand };
      brand.seoMeta = brand.seoMeta ? brand.seoMeta.trim() : "";
      brand.seoTitle = brand.seoTitle ? brand.seoTitle.trim() : "";

      if (brand.image) {
        brand.assetId = brand.image.id
      }

      delete brand.preview;

      const data = { ...brand }

      try {
        let response;
        if (!this.editing) response = await BrandService.save(data);
        else
          response = await BrandService.update(this.$route.params.id, data);

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({
            name: "config-brands-edit",
            params: { id: response.payload.id },
          });
          this.editing = true;
        }
      } catch (ex) {
        Alert.error(ex.errors.message);
      } finally {
        this.$store.dispatch("app/loading", false)
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await BrandService.get(id);
        this.brand = { ...response.payload };

        this.editing = true;
      } catch (ex) {
        let message = ex.errors.message;
        if (message == "No encontrado") message = Constants.Errors.RECORD_NOT_FOUND;
        Alert.error(message, () => this.$router.push({ name: "config-brands-list" }));
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  computed: {
    setOldFile: function () {
      if (this.brand.id && this.brand.image) {
        return [this.brand.image]
      } else {
        return []
      }
    }
  },
  created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>
